/**
 * SalesTemplateSettings — Personnalisation libre du modèle de document.
 *
 * 2-col : (gauche) accordion sections / (droite) aperçu PDF live avec debounce.
 * Sauvegarde live via PATCH /billing/document-template dès qu'un champ change.
 * Le logo passe par un endpoint dédié (multipart).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Palette, Image as ImageIcon, User, StickyNote, Loader2, Trash2,
  FileText, RotateCcw, AlertCircle, Building2, ArrowUpRight, Wand2, Droplet,
  Save, Plus,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_TPL = {
  accent_color: "#2563eb",
  logo_path: null,
  logo_width_mm: 30,
  seller_baseline: "",
  show_seller_fields: {
    legal_form: true, share_capital: true, address: true,
    siret: true, vat_number: true, rcs: true, ape: true,
    phone: true, email: true, website: true,
  },
  notes_default: "",
  table_zebra: true,
  table_style: "flat",
  card_border_width: "normal",
  show_seller_name_with_logo: false,
  watermark_style: "gray",
};

const COLOR_PRESETS = [
  { name: "Bleu nuit", value: "#2563eb" },
  { name: "Émeraude", value: "#059669" },
  { name: "Rose", value: "#e11d48" },
  { name: "Violet", value: "#7c3aed" },
  { name: "Ambre", value: "#d97706" },
  { name: "Ardoise", value: "#334155" },
  { name: "Rouge", value: "#dc2626" },
  { name: "Teal", value: "#0d9488" },
];

const TEMPLATE_PRESETS = [
  {
    key: "sober",
    name: "Sobre",
    hint: "Noir & blanc, minimaliste",
    swatch: ["#18181b", "#71717a", "#e4e4e7"],
  },
  {
    key: "colorful",
    name: "Coloré",
    hint: "Bleu vif, style cartes",
    swatch: ["#2563eb", "#60a5fa", "#dbeafe"],
  },
  {
    key: "minimal",
    name: "Minimal",
    hint: "Gris ardoise, sans filigrane",
    swatch: ["#475569", "#94a3b8", "#f1f5f9"],
  },
  {
    key: "corporate",
    name: "Corporate",
    hint: "Bleu marine, tous champs légaux",
    swatch: ["#1e3a8a", "#3b82f6", "#eff6ff"],
  },
];

const SELLER_FIELDS = [
  { key: "legal_form",    label: "Forme juridique (SAS, SARL...)" },
  { key: "share_capital", label: "Capital social" },
  { key: "address",       label: "Adresse postale" },
  { key: "siret",         label: "SIRET / SIREN" },
  { key: "vat_number",    label: "N° TVA intracommunautaire" },
  { key: "rcs",           label: "RCS (Registre du commerce)" },
  { key: "ape",           label: "Code APE / NAF" },
  { key: "email",         label: "Email de contact" },
  { key: "phone",         label: "Téléphone" },
];

export default function SalesTemplateSettings({ embedded = false } = {}) {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("invoices.write");
  const [loading, setLoading] = useState(true);
  const [tpl, setTpl] = useState(DEFAULT_TPL);
  const [hasLogo, setHasLogo] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [applyingPreset, setApplyingPreset] = useState(null);
  const [logoPalette, setLogoPalette] = useState([]);
  const [paletteLoading, setPaletteLoading] = useState(false);
  const [customPresets, setCustomPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState(null);
  const prevBlobUrl = useRef(null);
  const previewTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, cp] = await Promise.all([
        api.get("/billing/document-template"),
        api.get("/billing/document-template/custom-presets").catch(() => ({ data: { presets: [] } })),
      ]);
      setTpl({ ...DEFAULT_TPL, ...(r.data.template || {}) });
      setHasLogo(!!r.data.has_logo);
      setCustomPresets(cp.data?.presets || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-fetch palette dès qu'un logo est actif
  useEffect(() => {
    if (!hasLogo) { setLogoPalette([]); return; }
    (async () => {
      setPaletteLoading(true);
      try {
        const r = await api.get("/billing/document-template/logo-palette");
        setLogoPalette(r.data?.colors || []);
      } catch { setLogoPalette([]); }
      finally { setPaletteLoading(false); }
    })();
  }, [hasLogo]);

  const applyPreset = async (presetKey) => {
    if (!canWrite) return;
    setApplyingPreset(presetKey);
    try {
      const r = await api.post("/billing/document-template/apply-preset",
        { preset: presetKey });
      setTpl({ ...DEFAULT_TPL, ...(r.data.template || {}) });
      const p = TEMPLATE_PRESETS.find(x => x.key === presetKey);
      toast.success(`Preset "${p?.name || presetKey}" appliqué`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApplyingPreset(null);
    }
  };

  const applyCustomPreset = async (presetId) => {
    if (!canWrite) return;
    setApplyingPreset(presetId);
    try {
      const r = await api.post(`/billing/document-template/apply-custom-preset/${presetId}`);
      setTpl({ ...DEFAULT_TPL, ...(r.data.template || {}) });
      const p = customPresets.find(x => x.id === presetId);
      toast.success(`Preset "${p?.name || presetId}" appliqué`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApplyingPreset(null);
    }
  };

  const saveCurrentAsPreset = async () => {
    if (!canWrite) return;
    const name = window.prompt("Nom de ton preset (max 60 caractères) :");
    if (!name || !name.trim()) return;
    setSavingPreset(true);
    try {
      const r = await api.post("/billing/document-template/custom-presets",
        { name: name.trim() });
      setCustomPresets((cp) => [r.data.preset, ...cp]);
      toast.success(`Preset "${name.trim()}" enregistré`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingPreset(false);
    }
  };

  const deleteCustomPreset = async (presetId, name) => {
    if (!canWrite) return;
    if (!window.confirm(`Supprimer le preset "${name}" ?`)) return;
    try {
      await api.delete(`/billing/document-template/custom-presets/${presetId}`);
      setCustomPresets((cp) => cp.filter(p => p.id !== presetId));
      toast.success("Preset supprimé");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // ---- Live PDF preview (debounce 500ms) ----
  useEffect(() => {
    if (loading) return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewErr(null);
      try {
        const r = await api.post(
          "/billing/document-template/preview",
          { template: tpl },
          { responseType: "blob" },
        );
        const url = URL.createObjectURL(r.data);
        if (prevBlobUrl.current) URL.revokeObjectURL(prevBlobUrl.current);
        prevBlobUrl.current = url;
        setPdfUrl(url);
      } catch (e) {
        setPreviewErr(apiError(e));
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [tpl, loading]);

  useEffect(() => () => {
    if (prevBlobUrl.current) URL.revokeObjectURL(prevBlobUrl.current);
  }, []);

  // ---- Save one field (debounced) ----
  const saveField = useCallback(async (patch, keyLabel) => {
    if (!canWrite) return;
    setSavingKey(keyLabel);
    try {
      await api.patch("/billing/document-template", patch);
    } catch (e) {
      toast.error(apiError(e, "Impossible d'enregistrer"));
    } finally {
      setSavingKey(null);
    }
  }, [canWrite]);

  const setField = (k, v, label = k) => {
    setTpl((s) => ({ ...s, [k]: v }));
    saveField({ [k]: v }, label);
  };
  const setFieldNoSave = (k, v) => setTpl((s) => ({ ...s, [k]: v }));

  const setSellerVisibility = (fieldKey, val) => {
    setTpl((s) => ({
      ...s,
      show_seller_fields: { ...s.show_seller_fields, [fieldKey]: val },
    }));
    saveField(
      { show_seller_fields: { [fieldKey]: val } },
      `seller_field_${fieldKey}`,
    );
  };

  const uploadLogo = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Fichier trop lourd (>500 KB)");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/billing/document-template/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTpl({ ...DEFAULT_TPL, ...(r.data.template || {}) });
      setHasLogo(true);
      toast.success("Logo mis à jour");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
      ev.target.value = "";
    }
  };

  const removeLogo = async () => {
    if (!confirm("Supprimer le logo ?")) return;
    setUploading(true);
    try {
      const r = await api.delete("/billing/document-template/logo");
      setTpl({ ...DEFAULT_TPL, ...(r.data.template || {}) });
      setHasLogo(false);
      toast.success("Logo supprimé");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const resetAll = async () => {
    if (!confirm("Réinitialiser tous les paramètres du modèle ?")) return;
    setTpl(DEFAULT_TPL);
    await saveField({
      accent_color: DEFAULT_TPL.accent_color,
      logo_width_mm: DEFAULT_TPL.logo_width_mm,
      seller_baseline: "",
      show_seller_fields: DEFAULT_TPL.show_seller_fields,
      notes_default: "",
      table_zebra: true,
      watermark_style: "gray",
    }, "reset");
    toast.success("Modèle réinitialisé");
  };

  if (loading) {
    return <div className="h-8 w-72 bg-slate-100 animate-pulse rounded" />;
  }

  const Section = ({ icon: Icon, title, subtitle, children }) => (
    <Card className="p-5 border-slate-200 rounded-xl">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
          <Icon className="h-4 w-4 text-slate-700" />
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
      </div>
      {children}
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="sales-template-settings">
      {!embedded && (
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
              Modèle de document
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Personnalise le rendu de tes factures, devis et avoirs.
              Le modèle est partagé entre tous les types de documents.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={resetAll} disabled={!canWrite}
                  data-testid="template-reset-btn">
            <RotateCcw className="h-4 w-4 mr-1.5" />
            Réinitialiser
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_560px] gap-6">
        {/* ────────── COLONNE GAUCHE : réglages ────────── */}
        <div className="space-y-4">
          {/* PRESETS PRÊTS-À-L'EMPLOI */}
          <Section icon={Wand2} title="Presets prêts-à-l'emploi"
                   subtitle="Applique un modèle complet en 1 clic. Ton logo et tes notes sont conservés.">
            <div className="grid grid-cols-2 gap-3">
              {TEMPLATE_PRESETS.map((p) => (
                <button key={p.key} type="button"
                        onClick={() => applyPreset(p.key)}
                        disabled={!canWrite || applyingPreset === p.key}
                        className="flex flex-col gap-2 p-4 rounded-xl border-2 border-slate-200 hover:border-slate-900 hover:shadow-md transition text-left disabled:opacity-60 disabled:cursor-wait"
                        data-testid={`preset-${p.key}`}>
                  <div className="flex items-center gap-1.5">
                    {p.swatch.map((c) => (
                      <div key={c} className="h-5 w-5 rounded-full border border-white shadow-sm"
                           style={{ backgroundColor: c }} />
                    ))}
                    {applyingPreset === p.key && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-600 ml-1" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{p.hint}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Mes presets custom */}
            <div className="mt-5 pt-5 border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs font-semibold text-slate-800">Mes presets</div>
                  <div className="text-[10px] text-slate-500">
                    {customPresets.length}/10 preset(s) sauvegardé(s)
                  </div>
                </div>
                <Button size="sm" variant="outline"
                        onClick={saveCurrentAsPreset}
                        disabled={!canWrite || savingPreset || customPresets.length >= 10}
                        data-testid="save-current-as-preset-btn">
                  {savingPreset
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Save className="h-3.5 w-3.5 mr-1.5" />}
                  Enregistrer la config actuelle
                </Button>
              </div>
              {customPresets.length === 0 ? (
                <div className="text-[11px] text-slate-500 italic p-3 bg-slate-50 rounded-lg text-center">
                  Aucun preset personnalisé. Configure ton modèle puis clique "Enregistrer".
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {customPresets.map((cp) => (
                    <div key={cp.id}
                         className="relative flex flex-col gap-2 p-3 rounded-xl border-2 border-slate-200 hover:border-slate-900 hover:shadow-md transition"
                         data-testid={`custom-preset-${cp.id}`}>
                      <button type="button"
                              onClick={() => applyCustomPreset(cp.id)}
                              disabled={!canWrite || applyingPreset === cp.id}
                              className="flex flex-col gap-1.5 text-left disabled:opacity-60 disabled:cursor-wait">
                        <div className="flex items-center gap-1.5">
                          <div className="h-5 w-5 rounded-full border border-white shadow-sm"
                               style={{ backgroundColor: cp.accent_color || "#2563eb" }} />
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {cp.table_style === "cards" ? "Rectangles" : "Simple"}
                          </span>
                          {applyingPreset === cp.id && (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-600" />
                          )}
                        </div>
                        <div className="text-sm font-medium text-slate-900 truncate pr-6">
                          {cp.name}
                        </div>
                      </button>
                      <button type="button"
                              onClick={() => deleteCustomPreset(cp.id, cp.name)}
                              disabled={!canWrite}
                              className="absolute top-2 right-2 h-6 w-6 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center transition"
                              title="Supprimer"
                              data-testid={`delete-custom-preset-${cp.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Section>

          {/* IDENTITÉ VISUELLE */}
          <Section icon={Palette} title="Identité visuelle"
                   subtitle="Couleur d'accent + logo + baseline">
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-600 mb-2 block">Couleur d'accent</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PRESETS.map((p) => (
                    <button key={p.value} type="button"
                            onClick={() => setField("accent_color", p.value, "accent")}
                            disabled={!canWrite}
                            className={`h-9 w-9 rounded-full border-2 transition ${
                              tpl.accent_color === p.value
                                ? "border-slate-900 scale-110" : "border-white hover:border-slate-300"
                            }`}
                            style={{ backgroundColor: p.value }}
                            title={p.name}
                            data-testid={`color-preset-${p.value.slice(1)}`} />
                  ))}
                  <label className="h-9 w-9 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-slate-500">
                    <input id="tpl-accent-custom" name="tpl-accent-custom"
                           type="color" value={tpl.accent_color}
                           onChange={(e) => setFieldNoSave("accent_color", e.target.value)}
                           onBlur={(e) => saveField({ accent_color: e.target.value }, "accent")}
                           disabled={!canWrite}
                           className="opacity-0 w-full h-full cursor-pointer"
                           data-testid="color-custom" />
                  </label>
                </div>
                <div className="text-[10px] text-slate-500 mt-1 font-mono">{tpl.accent_color}</div>

                {/* Palette extraite du logo — auto-affichée si logo présent */}
                {hasLogo && (
                  <div className="mt-3 p-3 rounded-lg bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border border-violet-100"
                       data-testid="logo-palette-section">
                    <div className="flex items-center gap-2 mb-2">
                      <Droplet className="h-3.5 w-3.5 text-violet-600" />
                      <div className="text-[11px] font-semibold text-slate-800">
                        Couleurs extraites de ton logo
                      </div>
                      {paletteLoading && <Loader2 className="h-3 w-3 animate-spin text-slate-500" />}
                    </div>
                    {!paletteLoading && logoPalette.length === 0 && (
                      <div className="text-[10px] text-slate-500">
                        Aucune couleur exploitable détectée dans le logo.
                      </div>
                    )}
                    {logoPalette.length > 0 && (
                      <>
                        <div className="flex flex-wrap gap-2">
                          {logoPalette.map((c) => (
                            <button key={c} type="button"
                                    onClick={() => setField("accent_color", c, "accent")}
                                    disabled={!canWrite}
                                    className={`h-8 w-8 rounded-full border-2 transition ${
                                      tpl.accent_color === c
                                        ? "border-slate-900 scale-110" : "border-white hover:border-slate-300"
                                    }`}
                                    style={{ backgroundColor: c }}
                                    title={c}
                                    data-testid={`logo-color-${c.slice(1)}`} />
                          ))}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1.5">
                          Clique une couleur pour l'appliquer comme accent.
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs text-slate-600 mb-2 block">Logo (PNG, JPG, WebP — max 500 KB)</Label>
                {hasLogo ? (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <ImageIcon className="h-5 w-5 text-slate-500" />
                    <div className="flex-1 text-xs text-slate-600">
                      Logo actif — visible en haut à gauche du document.
                    </div>
                    <label className="cursor-pointer">
                      <Button size="sm" variant="outline" type="button" disabled={uploading || !canWrite} asChild>
                        <span>Remplacer</span>
                      </Button>
                      <input id="logo-replace-input" name="logo-replace-input"
                             type="file" accept="image/png,image/jpeg,image/webp"
                             onChange={uploadLogo} disabled={uploading || !canWrite}
                             className="hidden" data-testid="logo-replace-input" />
                    </label>
                    <Button size="sm" variant="outline" onClick={removeLogo}
                            disabled={uploading || !canWrite}
                            data-testid="logo-remove-btn"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-slate-300 rounded-lg hover:border-slate-500 hover:bg-slate-50 transition">
                    {uploading
                      ? <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                      : <ImageIcon className="h-6 w-6 text-slate-400" />}
                    <div className="text-xs text-slate-600">
                      {uploading ? "Envoi en cours…" : "Cliquer pour uploader"}
                    </div>
                    <input id="logo-upload-input" name="logo-upload-input"
                           type="file" accept="image/png,image/jpeg,image/webp"
                           onChange={uploadLogo} disabled={uploading || !canWrite}
                           className="hidden" data-testid="logo-upload-input" />
                  </label>
                )}
                {hasLogo && (
                  <div className="mt-3">
                    <Label className="text-xs text-slate-600 flex justify-between mb-1">
                      <span>Largeur du logo</span>
                      <span className="font-mono">{tpl.logo_width_mm}mm</span>
                    </Label>
                    <input id="logo-width-slider" name="logo-width-slider"
                           type="range" min={15} max={60} step={1}
                           value={tpl.logo_width_mm}
                           onChange={(e) => setFieldNoSave("logo_width_mm", Number(e.target.value))}
                           onMouseUp={(e) => saveField({ logo_width_mm: Number(e.target.value) }, "logo_width")}
                           onTouchEnd={(e) => saveField({ logo_width_mm: Number(e.target.value) }, "logo_width")}
                           disabled={!canWrite}
                           className="w-full accent-slate-900"
                           data-testid="logo-width-slider" />
                    <label className="flex items-center justify-between gap-3 mt-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                      <div>
                        <div className="text-xs font-medium text-slate-700">Afficher aussi le nom de l'entreprise</div>
                        <div className="text-[10px] text-slate-500">Le nom apparaît sous le logo (utile si le logo ne le contient pas)</div>
                      </div>
                      <Switch checked={!!tpl.show_seller_name_with_logo}
                              onCheckedChange={(v) => setField("show_seller_name_with_logo", v, "show_name_with_logo")}
                              disabled={!canWrite}
                              data-testid="show-name-with-logo-switch" />
                    </label>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs text-slate-600 mb-1 block" htmlFor="baseline">
                  Baseline (facultative, sous le logo/nom)
                </Label>
                <Input id="baseline" value={tpl.seller_baseline || ""}
                       placeholder="Ex. Solutions bureautiques depuis 1998"
                       maxLength={140}
                       onChange={(e) => setFieldNoSave("seller_baseline", e.target.value)}
                       onBlur={(e) => saveField({ seller_baseline: e.target.value }, "baseline")}
                       disabled={!canWrite}
                       data-testid="baseline-input" />
              </div>
            </div>
          </Section>

          {/* CHAMPS VENDEUR */}
          <Section icon={User} title="Champs vendeur affichés"
                   subtitle="Choisis ce qui apparaît sous le nom / logo">
            <div className="p-3 mb-3 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-2">
              <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-xs text-slate-700">
                Le <b>contenu</b> de ces champs (forme juridique SAS/SARL, SIRET, N° TVA...)
                se règle dans{" "}
                <a href="/app/settings/billing"
                   className="text-blue-700 font-medium underline hover:text-blue-900 inline-flex items-center gap-0.5"
                   data-testid="link-billing-settings">
                  Coordonnées légales
                  <ArrowUpRight className="h-3 w-3" />
                </a>.
                Ici tu contrôles uniquement ce qui <b>apparaît</b> sur le PDF.
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {SELLER_FIELDS.map((f) => (
                <label key={f.key}
                       className="flex items-center justify-between gap-3 p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <span className="text-xs text-slate-700">{f.label}</span>
                  <Switch checked={tpl.show_seller_fields?.[f.key] ?? true}
                          onCheckedChange={(v) => setSellerVisibility(f.key, v)}
                          disabled={!canWrite}
                          data-testid={`seller-field-${f.key}`} />
                </label>
              ))}
            </div>
          </Section>

          {/* STYLE TABLEAU + WATERMARK */}
          <Section icon={FileText} title="Style tableau & filigrane"
                   subtitle="Options tableau + comportement du BROUILLON">
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-600 mb-2 block">Style du tableau</Label>
                <RadioGroup value={tpl.table_style || "flat"}
                            onValueChange={(v) => setField("table_style", v, "table_style")}
                            disabled={!canWrite}
                            className="grid grid-cols-2 gap-2">
                  {[
                    { v: "flat",  label: "Simple", hint: "Traits fins entre lignes" },
                    { v: "cards", label: "Rectangles", hint: "Chaque ligne encadrée (style Axonaut)" },
                  ].map((opt) => (
                    <label key={opt.v}
                           className={`flex flex-col gap-0.5 p-3 rounded-lg border cursor-pointer transition ${
                             (tpl.table_style || "flat") === opt.v
                               ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"
                           }`}
                           data-testid={`table-style-${opt.v}`}>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value={opt.v} id={`ts-${opt.v}`} className="h-3.5 w-3.5" />
                        <span className="text-sm font-medium text-slate-800">{opt.label}</span>
                      </div>
                      <span className="text-[10px] text-slate-500 pl-5">{opt.hint}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <label className={`flex items-center justify-between gap-3 p-2.5 rounded-lg transition ${
                tpl.table_style === "cards" ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-50 cursor-pointer"
              }`}>
                <div>
                  <div className="text-xs font-medium text-slate-700">Zébrures alternées</div>
                  <div className="text-[10px] text-slate-500">
                    {tpl.table_style === "cards"
                      ? "Désactivé automatiquement en mode Rectangles"
                      : "Fond gris subtil une ligne sur deux"}
                  </div>
                </div>
                <Switch checked={!!tpl.table_zebra && tpl.table_style !== "cards"}
                        onCheckedChange={(v) => setField("table_zebra", v, "zebra")}
                        disabled={!canWrite || tpl.table_style === "cards"}
                        data-testid="table-zebra-switch" />
              </label>

              {/* Épaisseur des bordures — uniquement en mode Cards */}
              {tpl.table_style === "cards" && (
                <div data-testid="card-border-width-section">
                  <Label className="text-xs text-slate-600 mb-2 block">Épaisseur des bordures</Label>
                  <RadioGroup value={tpl.card_border_width || "normal"}
                              onValueChange={(v) => setField("card_border_width", v, "border_width")}
                              disabled={!canWrite}
                              className="grid grid-cols-3 gap-2">
                    {[
                      { v: "thin",   label: "Fine",    weight: "1px" },
                      { v: "normal", label: "Normale", weight: "2px" },
                      { v: "thick",  label: "Épaisse", weight: "3px" },
                    ].map((opt) => (
                      <label key={opt.v}
                             className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border cursor-pointer transition ${
                               (tpl.card_border_width || "normal") === opt.v
                                 ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"
                             }`}
                             data-testid={`border-width-${opt.v}`}>
                        <RadioGroupItem value={opt.v} id={`bw-${opt.v}`} className="h-3.5 w-3.5 sr-only" />
                        {/* Preview visuel : rectangle avec bordure de l'épaisseur choisie */}
                        <div className="h-6 w-full rounded border-slate-500 bg-white"
                             style={{ borderWidth: opt.weight, borderStyle: "solid" }} />
                        <span className="text-xs font-medium text-slate-700">{opt.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-600 mb-2 block">Filigrane BROUILLON</Label>
                <RadioGroup value={tpl.watermark_style}
                            onValueChange={(v) => setField("watermark_style", v, "watermark")}
                            disabled={!canWrite}
                            className="grid grid-cols-3 gap-2">
                  {[
                    { v: "gray", label: "Gris (défaut)" },
                    { v: "red", label: "Rouge" },
                    { v: "hidden", label: "Masqué" },
                  ].map((opt) => (
                    <label key={opt.v}
                           className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition ${
                             tpl.watermark_style === opt.v
                               ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"
                           }`}
                           data-testid={`watermark-${opt.v}`}>
                      <RadioGroupItem value={opt.v} id={`wm-${opt.v}`} className="h-3.5 w-3.5" />
                      <span className="text-xs text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </Section>

          {/* NOTES PAR DÉFAUT */}
          <Section icon={StickyNote} title="Notes par défaut"
                   subtitle="Auto-remplies sur chaque nouveau document (modifiables à la volée)">
            <Textarea id="notes_default" name="notes_default" value={tpl.notes_default || ""}
                      placeholder="Ex. Merci de votre confiance. Notre équipe reste à votre disposition."
                      rows={4} maxLength={2000}
                      onChange={(e) => setFieldNoSave("notes_default", e.target.value)}
                      onBlur={(e) => saveField({ notes_default: e.target.value }, "notes")}
                      disabled={!canWrite}
                      data-testid="notes-default-textarea" />
            <div className="text-[10px] text-slate-500 mt-1">
              {(tpl.notes_default || "").length}/2000
            </div>
          </Section>
        </div>

        {/* ────────── COLONNE DROITE : aperçu PDF ────────── */}
        <div className="lg:sticky lg:top-6 self-start">
          <Card className="border-slate-200 rounded-xl overflow-hidden bg-white h-[860px] flex flex-col"
                data-testid="template-pdf-preview">
            <div className="p-3 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
              <FileText className="h-4 w-4 text-slate-600" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">Aperçu du modèle</div>
                <div className="text-[10px] text-slate-500">
                  {previewLoading ? "Régénération…" : "Facture-démo mise à jour à chaque changement"}
                </div>
              </div>
              {(previewLoading || savingKey) && (
                <Loader2 className="h-4 w-4 animate-spin text-slate-600" />
              )}
            </div>
            <div className="flex-1 relative bg-slate-100">
              {previewErr && (
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                  <AlertCircle className="h-6 w-6 text-amber-500 mb-2" />
                  <div className="text-xs text-slate-600 mb-1">Aperçu indisponible</div>
                  <div className="text-[10px] text-slate-400 max-w-[280px]">{previewErr}</div>
                </div>
              )}
              {!pdfUrl && !previewErr && (
                <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-xs">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Génération de l'aperçu…
                </div>
              )}
              {pdfUrl && (
                <object data={pdfUrl} type="application/pdf"
                        className="w-full h-full" aria-label="Aperçu PDF">
                  <div className="p-4 text-xs text-slate-500 text-center">
                    Aperçu non supporté.
                    <a href={pdfUrl} download="apercu-modele.pdf"
                       className="ml-1 text-blue-600 underline">Télécharger</a>
                  </div>
                </object>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
